import Header from "@/components/Header";
import Seo from "@/components/Seo";
import ContactBar from "@/components/ContactBar";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";
import BookCallButton from "@/components/BookCall";
import Reveal from "@/components/Reveal";
import { mediaUrl, sz, txt, useContent } from "@/content/ContentContext";
import { buildAboutFaqs, resolveFaqs } from "@/content/faqData";
import { PAGE_META, breadcrumbSchema } from "@/content/seoData";
import workspace from "@/assets/workspace.jpg";

const About = () => {
  const c = useContent();
  const a = c.about;
  const image = a.image ? mediaUrl(a.image) : workspace;

  return (
    <div className="App">
      <Seo
        title={PAGE_META.about.title}
        description={PAGE_META.about.description}
        path="/about"
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <Header />
      <main>
        <section className="svcpage-hero">
          <Reveal className="svcpage-hero-inner" y={18}>
            <h1
              className="svcpage-title"
              style={sz(a.titleStart)}
              data-testid="page-title"
            >
              {txt(a.titleStart)}{" "}
              <span className="text-green" style={sz(a.titleAccent)}>
                {txt(a.titleAccent)}
              </span>
            </h1>
            <p className="svcpage-intro" style={sz(a.intro)}>
              {txt(a.intro)}
            </p>
            <BookCallButton
              className="btn-outline-light"
              testId="about-hero-cta"
            />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="about-hero-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </Reveal>
        </section>

        <section className="about-lead" id="about">
          <Reveal className="about-lead-copy" y={22}>
            <h2 className="section-title" style={sz(a.lead.title1)}>
              {txt(a.lead.title1)}
              <br />
              {txt(a.lead.title2)}{" "}
              <span className="text-green" style={sz(a.lead.accent)}>
                {txt(a.lead.accent)}
              </span>
            </h2>
            {(a.lead.paragraphs || []).map((paragraph, index) => (
              <p className="about-lead-para" key={index}>
                {paragraph}
              </p>
            ))}
          </Reveal>
          <Reveal className="about-lead-media" y={26} delay={0.12}>
            <img src={image} alt="Managed workspace" />
          </Reveal>
        </section>

        <section className="about-approach">
          <Reveal className="about-approach-head" y={20}>
            <h2 className="section-title" style={sz(a.approach.title)}>
              {txt(a.approach.title)}
            </h2>
            <p className="section-para" style={sz(a.approach.intro)}>
              {txt(a.approach.intro)}
            </p>
          </Reveal>
          <div className="about-approach-grid">
            {(a.approach.items || []).map((item, index) => (
              <Reveal
                as="article"
                className="about-approach-card"
                key={index}
                delay={index * 0.1}
                y={26}
                data-testid={`about-approach-${index}`}
              >
                <h3 style={sz(item.title)}>{txt(item.title)}</h3>
                <p style={sz(item.body)}>{txt(item.body)}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="about-values">
          <Reveal y={18}>
            <h2 className="section-title" style={sz(a.values.title)}>
              {txt(a.values.title)}
            </h2>
          </Reveal>
          <div className="about-values-grid">
            {(a.values.items || []).map((item, index) => (
              <Reveal
                as="article"
                className="about-values-card"
                key={index}
                delay={index * 0.08}
                y={22}
                data-testid={`about-value-${index}`}
              >
                <h3 style={sz(item.title)}>{txt(item.title)}</h3>
                <p style={sz(item.body)}>{txt(item.body)}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <Reveal as="section" className="cta-banner" y={20}>
          <div className="cta-banner-text">
            <h3 style={sz(a.banner.heading)}>{txt(a.banner.heading)}</h3>
            <p style={sz(a.banner.text)}>{txt(a.banner.text)}</p>
          </div>
          <div className="cta-banner-actions">
            <BookCallButton
              className="btn-outline-light"
              testId="about-banner-cta"
            />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="about-banner-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </div>
        </Reveal>
      </main>
      <FAQSection items={resolveFaqs(c, "about", buildAboutFaqs(c))} />
      <ContactForm />
      <ContactBar />
      <Footer />
    </div>
  );
};

export default About;
